import { useEffect, useState, type DragEvent } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  cancelTransfer,
  deleteEntries,
  fetchRoots,
  transferEntries,
  type RootSummary,
} from '../api'
import { useActivity } from '../activity'
import { useAuth } from '../auth'
import { carriesEntries, unpackEntryDrag, wantsCopy } from '../entry-drag'
import { formatBytes } from '../format'
import { UndoBanner, useUndo } from '../undo'
import ActivityPanel from './ActivityPanel'

export default function Layout() {
  const { user, signOut } = useAuth()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { offer: offerUndo } = useUndo()
  const { track } = useActivity()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [dropRoot, setDropRoot] = useState<string | null>(null)

  const roots = useQuery({
    queryKey: ['roots'],
    queryFn: fetchRoots,
    // Nodes go up and down; keep the online dots honest without a socket.
    refetchInterval: 15_000,
  })

  const single = roots.data?.length === 1
  const soleRoot = single ? roots.data?.[0] : undefined

  // On a phone the sidebar covers the page, so navigating has to close it.
  useEffect(() => setDrawerOpen(false), [location.pathname])

  /**
   * 저장소 이름 위에 놓으면 그리로 옮긴다. 다른 기계여도 된다 — 그때는 바이트가 허브를
   * 거쳐 흐르므로 시간이 걸리고, 끝나면 한 줄로 알린다.
   *
   * 여기서는 무엇을 끌고 있는지 드래그에 실린 것으로만 알 수 있다. 목록 쪽 ref 는 다른
   * 컴포넌트의 것이라 보이지 않는다.
   */
  function rootDropZone(target: RootSummary) {
    if (target.permission === 'read' || !target.online) return {}

    return {
      onDragOver: (event: DragEvent<HTMLElement>) => {
        if (!carriesEntries(event.dataTransfer)) return
        event.preventDefault()
        event.dataTransfer.dropEffect = wantsCopy(event) ? 'copy' : 'move'
        setDropRoot(target.id)
      },
      onDragLeave: () => setDropRoot(null),
      onDrop: (event: DragEvent<HTMLElement>) => {
        event.preventDefault()
        setDropRoot(null)
        const dragged = unpackEntryDrag(event.dataTransfer)
        if (!dragged) return
        void moveToRoot(dragged, target, wantsCopy(event) ? 'copy' : 'move')
      },
    }
  }

  async function moveToRoot(
    dragged: { rootId: string; path: string; names: string[] },
    target: RootSummary,
    mode: 'move' | 'copy',
  ) {
    const paths = dragged.names.map((name) =>
      dragged.path === '/' ? `/${name}` : `${dragged.path}/${name}`,
    )

    /**
     * 멈추라는 말은 허브가 이 일에 붙여준 이름표로 간다. 그 이름표는 첫 줄로 오므로,
     * 그전에 취소를 누른 경우를 위해 눌렀다는 사실만 기억해 두었다가 이름표가 오면
     * 그때 보낸다 — 누른 것이 아무 일도 아닌 것이 되지 않게.
     */
    let transferId: string | null = null
    let stopWanted = false
    const stop = () => {
      if (transferId === null) {
        stopWanted = true
        return
      }
      void cancelTransfer(target.id, transferId)
    }

    // 오래 걸릴 수 있는 일이므로 업로드와 같은 자리에 얹어둔다. 화면을 옮겨 다녀도
    // 남아 있고, 무엇이 건너가는 중인지 그때그때 보인다.
    const job = track(
      `${target.label}(으)로 ${mode === 'move' ? '옮기는' : '복사하는'} 중`,
      target.id,
      '/',
      stop,
    )

    const result = await transferEntries(
      { toRootId: target.id, fromRootId: dragged.rootId, paths, into: '/', mode },
      (progress) =>
        job.progress(
          progress.bytes,
          `${progress.files}개 · ${formatBytes(progress.bytes)} · ${progress.current}`,
        ),
      (id) => {
        transferId = id
        if (stopWanted) stop()
      },
    ).catch((err: unknown) => {
      const message = err instanceof Error ? err.message : String(err)
      job.finish(message)
      offerFailure(message)
      return null
    })
    if (result === null) return

    job.finish()

    void queryClient.invalidateQueries()

    const verb = mode === 'move' ? '옮겼습니다' : '복사했습니다'
    const trashed = result.trashedOriginals ? ' 원본은 보내온 쪽 휴지통에 있습니다.' : ''
    const missed =
      result.failed.length > 0 ? ` ${result.failed.length}개는 실패: ${result.failed.join(', ')}` : ''
    // 멈춘 전송도 도착한 것은 도착한 것이다. 몇 개가 건너간 뒤였는지 말해주지 않으면
    // 사람이 양쪽을 열어 세어봐야 한다.
    const stopped = result.cancelled ? '멈췄습니다. ' : ''

    if (result.arrived.length > 0) {
      offerUndo({
        message: `${stopped}${result.arrived.length}개를 ${target.label}(으)로 ${verb}.${trashed}${missed}`,
        // 되돌리기는 온 길을 되짚는 것이다. 복사였다면 도착한 것만 지운다.
        undo: () =>
          mode === 'copy'
            ? deleteEntries(target.id, result.arrived)
            : transferEntries({
                toRootId: dragged.rootId,
                fromRootId: target.id,
                paths: result.arrived,
                into: dragged.path,
                mode: 'move',
              }),
      })
    } else if (result.cancelled) {
      // 되돌릴 것이 없다 — 원본은 그대로다. 다만 반쯤 건너간 폴더는 말해줘야 한다.
      const half =
        result.partial.length > 0
          ? ` ${result.partial.join(', ')}은(는) ${result.filesSent}개 파일까지 건너간 뒤라 ${target.label} 쪽에 그만큼 들어 있습니다. 원본은 그대로입니다.`
          : ''
      offerUndo({ message: `멈췄습니다.${half}`, undo: async () => undefined })
    } else {
      offerFailure(result.failed.join(', '))
    }
  }

  function offerFailure(message: string) {
    offerUndo({ message: `옮기지 못했습니다 — ${message}`, undo: async () => undefined })
  }

  return (
    <div className="shell">
      <header className="topbar">
        <button
          type="button"
          className="drawer-toggle"
          aria-label="메뉴 열기"
          aria-expanded={drawerOpen}
          onClick={() => setDrawerOpen((open) => !open)}
        >
          <span aria-hidden="true">☰</span>
        </button>
        <NavLink to="/" className="brand-link">
          Arthur Storage
        </NavLink>
      </header>

      {drawerOpen && (
        <div className="drawer-backdrop" role="presentation" onClick={() => setDrawerOpen(false)} />
      )}

      <aside className={`sidebar ${drawerOpen ? 'open' : ''}`}>
        <div className="brand">
          <NavLink to="/">Arthur Storage</NavLink>
        </div>

        <nav className="roots">
          {/* A list of one is just noise; show it as a header instead. Adding a
              second node brings the list back on its own. */}
          {!single && <h2>저장소</h2>}
          {roots.isPending && <p className="muted small">불러오는 중…</p>}
          {roots.data?.length === 0 && (
            <p className="muted small">
              사용 가능한 저장소가 없습니다.
              {user?.role === 'admin' ? ' 관리에서 노드를 추가하세요.' : ' 관리자에게 문의하세요.'}
            </p>
          )}

          {/* Still a link: clicking it is how you get back to the top folder. */}
          {soleRoot && (
            <NavLink
              to={`/r/${encodeURIComponent(soleRoot.id)}/`}
              className={`root-link sole-root ${dropRoot === soleRoot.id ? 'drop-target' : ''}`}
              end
              {...rootDropZone(soleRoot)}
            >
              <span className="root-body">
                <span className="root-label">
                  <span className={`dot ${soleRoot.online ? 'online' : 'offline'}`} />
                  {soleRoot.label}
                </span>
                <span className="root-meta">
                  {soleRoot.online ? '온라인' : '오프라인'}
                  {soleRoot.permission === 'read' && ' · 읽기 전용'}
                </span>
                <QuotaBar root={soleRoot} />
              </span>
            </NavLink>
          )}

          {!single &&
            roots.data?.map((root) => (
              <NavLink
                key={root.id}
                to={`/r/${encodeURIComponent(root.id)}/`}
                className={`root-link ${dropRoot === root.id ? 'drop-target' : ''}`}
                {...rootDropZone(root)}
              >
                <span className={`dot ${root.online ? 'online' : 'offline'}`} />
                <span className="root-body">
                  <span className="root-label">{root.label}</span>
                  <span className="root-meta">
                    {root.kind === 'share' ? `${root.nodeName} · ` : ''}
                    {root.permission === 'read' ? '읽기 전용' : '읽기·쓰기'}
                  </span>
                  <QuotaBar root={root} />
                </span>
              </NavLink>
            ))}
        </nav>

        <div className="sidebar-footer">
          <NavLink to="/links" className="nav-item">
            공유 링크
          </NavLink>
          <NavLink to="/trash" className="nav-item">
            휴지통
          </NavLink>
          {user?.role === 'admin' && (
            <NavLink to="/admin" className="nav-item">
              관리
            </NavLink>
          )}
          <NavLink to="/account" className="nav-item">
            {user?.displayName}
          </NavLink>
          <button type="button" className="nav-item as-button" onClick={() => void signOut()}>
            로그아웃
          </button>
        </div>
      </aside>

      <main className="content">
        {/* 목록에서 한 일도, 사이드바에 끌어다 놓은 일도 여기 한 자리에 뜬다. */}
        <UndoBanner />
        <Outlet />
      </main>

      <ActivityPanel />
    </div>
  )
}

function QuotaBar({ root }: { root: RootSummary }) {
  const used = root.quota.usedBytes
  const limit = root.quota.limitBytes ?? root.quota.nodeLimitBytes
  const free = root.diskFreeBytes

  // No cap means nothing measured the tree, so do not present a used figure
  // that would just be a stale zero. Free space comes from `df` and is real.
  if (limit === null) {
    return (
      <span className="root-meta">{free !== null ? `${formatBytes(free)} 여유` : ''}</span>
    )
  }

  const ratio = Math.min(1, used / limit)
  const level = ratio >= 0.95 ? 'full' : ratio >= 0.8 ? 'warn' : 'ok'

  return (
    <>
      <span className="quota-track">
        <span className={`quota-fill ${level}`} style={{ width: `${ratio * 100}%` }} />
      </span>
      <span className="root-meta">
        {formatBytes(used)} / {formatBytes(limit)}
      </span>
    </>
  )
}
